import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { IconLightbulb, IconLock, IconRefresh, IconSettings, IconZap } from '../Icons';
import TradeInsightsSettings from './TradeInsightsSettings';
import './TradeInsightsPanel.css';

/**
 * Patterns in the signed-in person's own closed trades (/api/trade-insights):
 * best and worst weekdays, economic-release days, options strategies…
 *
 * Until they turn insights on they see the first card and how many more
 * there are, with the notice they accept to see the rest. Shows nothing when
 * the feature is off for them or they have no connected accounts.
 */

export const NOTICE = 'These insights describe patterns in your own past trades. They’re for information only: not financial advice and not a recommendation to buy or sell anything. Past results don’t predict future ones.';

function ago(iso) {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return null;
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

const shortDay = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

export default function TradeInsightsPanel({ refreshKey = 0, compact = false }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async ({ refresh = false } = {}) => {
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINTS.tradeInsights}${refresh ? '?refresh=1' : ''}`, { headers: getBearerAuthHeaders() });
      const body = await res.json().catch(() => ({}));
      if (res.status === 403 || res.status === 401) { setHidden(true); return; }
      if (!res.ok || !body.success) throw new Error(body.error || 'Couldn’t load your insights.');
      setData(body);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setConsent = async (on) => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.tradeInsightsConsent, {
        method: 'POST',
        headers: { ...getBearerAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ on }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.error || 'Couldn’t save that.');
      setData(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    await load({ refresh: true });
    setBusy(false);
  };

  if (compact && hidden) return <p className="ti-empty">Insights aren’t included on your account yet.</p>;
  if (compact && data?.status === 'no_accounts') return <p className="ti-empty">Connect a broker in My Portfolio to see insights from your trades.</p>;
  if (hidden || (!data && !error) || data?.status === 'no_accounts') return null;

  const cards = data?.cards || [];
  return (
    <section className={`ti-panel${compact ? ' is-compact' : ''}`} aria-label="Insights from your trades">
      <header className="ti-head">
        <h3><IconZap size={16} /> Insights from your trades</h3>
        <span className="ti-head-actions">
          {!compact ? (
            <button type="button" className="ti-link" onClick={() => setShowSettings((v) => !v)} aria-expanded={showSettings}>
              <IconSettings size={14} /> Settings
            </button>
          ) : null}
          {data?.consented ? (
            <button type="button" className="ti-link" onClick={refresh} disabled={busy} title="Rebuild from your latest trades (once an hour)">
              <IconRefresh size={14} /> Refresh
            </button>
          ) : null}
        </span>
      </header>

      {showSettings ? <TradeInsightsSettings onChanged={() => load()} /> : null}

      {error ? <p className="ti-error">{error}</p> : null}

      {data?.status === 'not_enough' ? (
        <p className="ti-empty">
          Insights appear once you have {data.minTrades} closed trades
          {data.tradeCount ? ` (you have ${data.tradeCount} so far)` : ''}. With fewer, a pattern is more likely luck than habit.
        </p>
      ) : null}

      {data?.status === 'ok' && !cards.length ? (
        <p className="ti-empty">Nothing stands out in your trades right now. We’ll look again as you trade.</p>
      ) : null}

      {data?.review ? (
        <div className="ti-review">
          <div className="ti-review-head">
            <strong>Your week</strong>
            <span>{shortDay(data.review.from)} – {shortDay(data.review.to)}</span>
          </div>
          <ul>
            {data.review.lines.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      ) : null}

      {cards.length ? (
        <ul className="ti-cards">
          {cards.map((c) => (
            <li key={c.key} className="ti-card">
              <strong>{c.title}</strong>
              <p>{c.body}</p>
              {c.tip ? <p className="ti-tip"><IconLightbulb size={14} /> {c.tip}</p> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {data && !data.consented && data.status === 'ok' && cards.length ? (
        <div className="ti-consent">
          <p className="ti-consent-lead">
            <IconLock size={14} />
            {data.lockedCount
              ? ` ${data.lockedCount} more insight${data.lockedCount === 1 ? '' : 's'} from your trades, refreshed as you trade.`
              : ' Keep getting insights like this as you trade.'}
          </p>
          <p className="ti-notice">{NOTICE}</p>
          <button type="button" className="ti-btn" onClick={() => setConsent(true)} disabled={busy}>
            {busy ? 'Saving…' : 'I understand, turn on insights'}
          </button>
        </div>
      ) : null}

      {data?.consented ? (
        <footer className="ti-foot">
          <span>Patterns in your past trades · not financial advice{data.computedAt ? ` · updated ${ago(data.computedAt)}` : ''}</span>
          <button type="button" className="ti-link" onClick={() => setConsent(false)} disabled={busy}>Turn off</button>
        </footer>
      ) : null}
    </section>
  );
}
